import {
	Button,
	ChipSelect,
	EmptyState,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useAccess } from "~/core/access/index";
import { ApiError, type AudienceReport, api, METRICS, type MetricName } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Bars, Series, value } from "~/modules/analytics/components/chart";
import { Drill } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";

export function AudienceInsights({ kind, ...props }: ReadingProps & { kind: "personas" }) {
	const { entityId, filters } = props;
	const t = useT();
	const { locale } = useI18n();
	const [metric, setMetric] = useState<MetricName>("visibility");
	const [selected, setSelected] = useState("");
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "insights", kind, filters],
		queryFn: () => api.insights.audiences(entityId, kind, filters),
		...read,
	});
	return (
		<StageShell
			{...props}
			reading={kind}
			lede={t(`audience.${kind}.help`)}
			actions={
				<Button asChild variant="default">
					<Link to={`/w/${entityId}/settings/audiences`}>{t("audience.configure")}</Link>
				</Button>
			}
		>
			<Async state={state}>
				{(data) => {
					const rows = [...data.rows].sort((a, b) => {
						const av = a.metrics.find((m) => m.metric === metric)?.value;
						const bv = b.metrics.find((m) => m.metric === metric)?.value;
						if (av == null) return bv == null ? a.name.localeCompare(b.name) : 1;
						if (bv == null) return -1;
						return metric === "average_position" ? av - bv : bv - av;
					});
					const focused = rows.find((r) => r.id === selected) ?? rows[0];
					const measured = rows.filter((r) => r.samples > 0);
					return (
						<>
							{!data.sampling_available && (
								<p className="form-note" role="status">
									{t("audience.regionPending")}
								</p>
							)}
							<Section
								title={t("audience.comparison")}
								lede={t("audience.comparisonHelp")}
								action={
									<ChipSelect
										value={metric}
										onChange={(v) => setMetric(v as MetricName)}
										aria-label={t("platforms.metric")}
										options={METRICS.map((m) => ({ value: m, label: t(`metric.${m}`) }))}
									/>
								}
							>
								{rows.length === 0 ? (
									<EmptyState size="compact" title={t(`audience.${kind}.empty`)} />
								) : (
									<>
										{measured.length > 0 && (
											<Bars
												metric={metric}
												rows={measured.map((r) => ({
													key: r.id,
													label: r.name,
													value: r.metrics.find((m) => m.metric === metric)?.value ?? null,
												}))}
											/>
										)}
										<div className="table-scroll">
											<Table variant="resource">
												<TableHeader>
													<TableRow>
														<TableHead>{t(`${kind}.axis`)}</TableHead>
														<TableHead className="num">{t("audience.samples")}</TableHead>
														<TableHead className="num">{t(`metric.${metric}`)}</TableHead>
														<TableHead className="num">{t("audience.previous")}</TableHead>
														<TableHead>{t("audience.actions")}</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{rows.map((row) => (
														<TableRow key={row.id}>
															<TableCell>
																<Button
																	variant="link"
																	className="h-auto p-0 text-left"
																	onClick={() => setSelected(row.id)}
																	aria-pressed={focused?.id === row.id}
																>
																	{row.name}
																</Button>
															</TableCell>
															<TableCell className="num mono">{row.samples}</TableCell>
															<TableCell className="num mono">
																{value(locale, metric, row.metrics.find((m) => m.metric === metric)?.value)}
															</TableCell>
															<TableCell className="num mono">
																{value(locale, metric, row.metrics.find((m) => m.metric === metric)?.previous)}{" "}
																<span className="faint">· {row.previous_samples}</span>
															</TableCell>
															<TableCell>
																{row.samples > 0 ? (
																	<Drill
																		view={{
																			kind: "answers",
																			label: row.name,
																			filters: {
																				...filters,
																				persona: row.id,
																			},
																		}}
																	>
																		{t("audience.answers")}
																	</Drill>
																) : (
																	<span className="faint">{t("audience.notSampled")}</span>
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									</>
								)}
								{data.unassigned_samples > 0 && (
									<p className="hint">
										{t(`audience.${kind}.unassigned`)} · {data.unassigned_samples}
									</p>
								)}
							</Section>
							{focused && (
								<Section
									title={`${focused.name} · ${t("audience.trend")}`}
									action={
										kind === "personas" && data.definitions.some((a) => a.id === focused.id && !a.archived) ? (
											<PersonaSample
												key={`${focused.id}:${filters.locale}`}
												entityId={entityId}
												contentLocale={filters.locale}
												persona={focused.id}
											/>
										) : undefined
									}
								>
									{focused.samples === 0 ? (
										<EmptyState
											size="compact"
											title={t("audience.trendEmptyTitle")}
											description={t("audience.trendEmpty")}
										/>
									) : (
										<AudienceTrend row={focused} metric={metric} />
									)}
								</Section>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}
function AudienceTrend({ row, metric }: { row: AudienceReport["rows"][number]; metric: MetricName }) {
	return (
		<Series
			metric={metric}
			assets={[
				{ entity_id: row.id, name: row.name, domain: null, is_you: true, value: null, previous: null, rank: null },
			]}
			points={row.trend.map((p) => ({ bucket: p.date, entity_id: row.id, value: p.metrics[metric] }))}
		/>
	);
}
function PersonaSample({
	entityId,
	contentLocale,
	persona,
}: {
	entityId: string;
	contentLocale: string;
	persona: string;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, subscribed, why } = useAccess();
	const cache = useQueryClient();
	const sample = useMutation({
		mutationFn: () => api.samplePersona(entityId, contentLocale, persona),
		onSuccess: () => cache.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	return (
		<div className="stack">
			<Button
				variant="default"
				disabled={!editor || !subscribed || sample.isPending}
				title={!editor ? why.editor : undefined}
				onClick={() => sample.mutate()}
			>
				{t(sample.isPending ? "audience.saving" : "audience.sample")}
			</Button>
			{!sample.data && <span className="hint">{t("audience.cost")}</span>}
			{sample.error && (
				<p role="alert">{sample.error instanceof ApiError ? sample.error.localized(locale) : t("audience.error")}</p>
			)}
			{sample.data && (
				<Link to={`/w/${entityId}/tasks/loop`}>
					{t(sample.data.status === "skipped" ? "audience.skipped" : "audience.task")}
				</Link>
			)}
		</div>
	);
}
export function InsightPersonas(props: ReadingProps) {
	return <AudienceInsights {...props} kind="personas" />;
}
